function getTransformItems(mappingsStr) {
    if (!mappingsStr) return undefined;

    const mappings = {};
    const lines = mappingsStr.split(/\r?\n/);
    lines.forEach((line) => {
        const parts = line.split(':');
        if (parts.length >= 2) {
            const key = parts.shift().trim().toLowerCase();
            const val = parts.join(':').trim();
            if (key) {
                mappings[key] = val;
            }
        }
    });

    if (Object.keys(mappings).length === 0) return undefined;

    return function (items) {
        return items
            .map((item) => {
                const lookupKey = (item.label || '').toString().toLowerCase();
                const mappedVal = mappings[lookupKey];
                if (mappedVal !== undefined) {
                    if (mappedVal.toLowerCase() === '[hidden]') {
                        return null;
                    }
                    let highlighted = mappedVal;
                    if (item.highlighted && item.highlighted.includes('<mark>')) {
                        const match = item.highlighted.match(/<mark>(.*?)<\/mark>/i);
                        if (match && match[1]) {
                            const query = match[1];
                            const escapedQuery = query.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
                            const regex = new RegExp(`(${escapedQuery})`, 'gi');
                            highlighted = mappedVal.replace(regex, '<mark>$1</mark>');
                        }
                    }
                    return {
                        ...item,
                        label: mappedVal,
                        highlighted,
                    };
                }
                return item;
            })
            .filter(Boolean);
    };
}

export function setupFacets(postType, containerEl, refinementList, rangeSlider, panel) {
    const config = window.snapFindPublic;
    const postSchema = config.schema[postType] || [];
    const widgets = [];

    const facetFields = postSchema.filter((f) => f.facet === 'yes' && f.facet_display !== 'no');

    facetFields.forEach((field) => {
        // Create an element for this specific facet module
        const mount = document.createElement('div');
        mount.className = 'snaf-facet-mount';
        containerEl.appendChild(mount);

        // Map backend config values (with fallbacks)
        let isCollapsed = field.facet_initial_state === 'collapsed';

        if (isCollapsed) {
            const urlParams = new URLSearchParams(window.location.search);
            for (const key of urlParams.keys()) {
                // Check if the URL contains a refinement for this specific facet slug
                if (key.includes(`[${field.field_slug}]`) || key === field.field_slug) {
                    isCollapsed = false;
                    break;
                }
            }
        }

        // Custom Panel wrapper handling header & empty visibility
        const facetPanel = panel({
            collapsed: (options) => {
                // If it's a completely fresh state and the facet hasn't been interacted with, respect backend default.
                // We could just return `isCollapsed`. InstantSearch keeps track of local UI collapses natively if you click the button.
                return isCollapsed;
            },
            hidden: ({ canRefine }) => !canRefine,
            templates: {
                header: () => field.display_name,
                collapseButtonText: ({ collapsed }) =>
                    collapsed
                        ? `
                    <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" stroke-width="2" fill="none"><polyline points="6 9 12 15 18 9"></polyline></svg>
                `
                        : `
                    <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" stroke-width="2" fill="none"><polyline points="18 15 12 9 6 15"></polyline></svg>
                `,
            },
            cssClasses: {
                root: 'snaf-facet-wrapper',
                header: 'snaf-facet-title',
                collapseButton: 'snaf-facet-title',
            },
        });

        const facetType = field.facet_type || 'checkbox';
        const isNumeric =
            field.field_type === 'float' ||
            field.field_type === 'int32' ||
            field.field_type === 'int64';

        const transformItems = getTransformItems(field.facet_mappings);

        if (facetType === 'range' && isNumeric) {
            const rangeWidgetWithPanel = facetPanel(rangeSlider);
            widgets.push(
                rangeWidgetWithPanel({
                    container: mount,
                    attribute: field.field_slug,
                    pips: false,
                    tooltips: true,
                })
            );
        } else if (facetType === 'button') {
            mount.classList.add('snaf-facet-type-button');
            const listWidgetWithPanel = facetPanel(refinementList);
            const sortOrder =
                field.facet_sort === 'alphabetical'
                    ? ['isRefined', 'name:asc']
                    : ['isRefined', 'count:desc', 'name:asc'];
            const limit = parseInt(field.facet_min, 10) || 5;
            const showMoreLimit = parseInt(field.facet_max, 10) || 50;

            const widgetConfig = {
                container: mount,
                attribute: field.field_slug,
                searchable: true,
                searchablePlaceholder: `Search ${field.display_name}...`,
                showMore: true,
                limit,
                showMoreLimit,
                sortBy: sortOrder,
            };
            if (transformItems) {
                widgetConfig.transformItems = transformItems;
            }

            widgets.push(listWidgetWithPanel(widgetConfig));
        } else {
            // Default: checkbox list (also fallback if range is chosen on a string field)
            const listWidgetWithPanel = facetPanel(refinementList);
            const sortOrder =
                field.facet_sort === 'alphabetical'
                    ? ['isRefined', 'name:asc']
                    : ['isRefined', 'count:desc', 'name:asc'];
            const limit = parseInt(field.facet_min, 10) || 5;
            const showMoreLimit = parseInt(field.facet_max, 10) || 20;

            const widgetConfig = {
                container: mount,
                attribute: field.field_slug,
                searchable: true,
                searchablePlaceholder: `Search ${field.display_name}...`,
                showMore: true,
                limit,
                showMoreLimit,
                sortBy: sortOrder,
            };
            if (transformItems) {
                widgetConfig.transformItems = transformItems;
            }

            widgets.push(listWidgetWithPanel(widgetConfig));
        }
    });

    return widgets;
}

export function scrollToSnapfindAppTop(offset = 0, duration = 900) {
    // Cancel any in-progress scroll animation before starting a new one.
    if (scrollToSnapfindAppTop._frameId) {
        cancelAnimationFrame(scrollToSnapfindAppTop._frameId);
        scrollToSnapfindAppTop._frameId = null;
    }

    const startY = window.scrollY || window.pageYOffset;
    if (startY < 2) return;

    const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

    let startTime = null;
    const step = (timestamp) => {
        if (startTime === null) startTime = timestamp;
        const elapsed = timestamp - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const y = startY * (1 - easeInOutCubic(progress));

        window.scrollTo(0, y);

        if (progress < 1) {
            scrollToSnapfindAppTop._frameId = window.requestAnimationFrame(step);
        } else {
            scrollToSnapfindAppTop._frameId = null;
        }
    };

    scrollToSnapfindAppTop._frameId = window.requestAnimationFrame(step);
}
scrollToSnapfindAppTop._frameId = null;
